import type { Exception, Event as SentryEvent } from "@sentry/types";
import { getPosthogClient } from "./posthog";
import { ENVS } from "@v2/shared/environment.config";

/**
 * AIDEV-NOTE: Mirroring vs Dual Logging
 * Mirroring forwards Sentry exceptions to PostHog as `$exception` with a namespaced
 * fingerprint (`mirror:|...`). Dual logging is PostHog's native exception capture.
 * Prefer mirroring to avoid duplicates; keep dual logging disabled unless testing.
 */

/** Constants */
export const MIRROR_PREFIX = "mirror:" as const;

// AIDEV-NOTE: prevent duplicate PostHog mirrors within the same iframe session
const seenExceptionFingerprints = new Map<string, number>();
const MAX_SEEN_FINGERPRINTS = 200;

function rememberFingerprint(fingerprint: string) {
  seenExceptionFingerprints.set(fingerprint, Date.now());
  if (seenExceptionFingerprints.size > MAX_SEEN_FINGERPRINTS) {
    const [oldest] = seenExceptionFingerprints.keys();
    if (oldest) {
      seenExceptionFingerprints.delete(oldest);
    }
  }
}

function hasSeenFingerprint(fingerprint: string | undefined) {
  if (!fingerprint) return false;
  return seenExceptionFingerprints.has(fingerprint);
}

/** Types & Interfaces */
export type RuntimeMode = "standalone" | "embedded";

/**
 * AIDEV-NOTE: Single entrypoint for mirroring exceptions to PostHog.
 * Uses original Error when available (best fidelity). Otherwise reconstructs a V8‑like
 * Error from Sentry payload so PostHog can parse name/message/stack reliably.
 */
export function captureExceptionForPosthog(
  original: unknown,
  scrubbedEvent: SentryEvent
) {
  const client = getPosthogClient();

  const first = getFirstException(scrubbedEvent);
  const frames = getFrames(first);
  const { name, message, stackFromOriginal } = deriveNameAndMessage(
    original,
    first,
    scrubbedEvent.message
  );
  const stackLines =
    original instanceof Error ? undefined : v8StackLines(frames);
  const err = ensureErrorObject(original, name, message, stackLines);

  const topInfo = getTopFrameInfo(frames);
  const fingerprint = buildMirrorFingerprint({
    name,
    message,
    env: ENVS.environment,
    mode: runtimeModeFromTags(
      scrubbedEvent.tags as Record<string, unknown> | undefined
    ),
    host: getWindowHost(),
    pathname: getWindowPathname(),
    top: topInfo,
  });

  const normalizedMessage = message.replace(/\d+/g, "#");
  const dedupeKey = fingerprint || `${name}|${normalizedMessage}`;
  if (hasSeenFingerprint(dedupeKey)) {
    return;
  }
  rememberFingerprint(dedupeKey);

  try {
    client.captureException(err as unknown as Error, {
      $exception_message: message,
      $exception_type: name,
      $exception_stack_trace: stackFromOriginal ?? err.stack,
      $exception_fingerprint: fingerprint,

      origin: "sentry-mirror",
      error_type: "error_client",
      sentry_event_id: scrubbedEvent.event_id,
      sentry_trace_id: scrubbedEvent.contexts?.trace?.trace_id,
      environment: scrubbedEvent.environment,
      release: scrubbedEvent.release,
      route: getWindowPathname(),
      ...(scrubbedEvent.tags as Record<string, unknown>),
      ...(scrubbedEvent.extra as Record<string, unknown>),
    });
  } catch (err2) {
    if (process.env.ENVIRONMENT !== "production") {
      console.warn("[PostHog] captureExceptionForPosthog failed", err2);
    }
  }
}

// Private Last
/**
 * AIDEV-NOTE: Sentry frame shape used to reconstruct V8-like stacks for grouping.
 */
type SentryFrame = {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
};

/**
 * AIDEV-NOTE: Build a stable, namespaced fingerprint used by PostHog error grouping.
 * - Normalizes digits in message (→ '#') to avoid time/id churn.
 * - Includes runtime mode, host, path, env and top frame for precision.
 */
function buildMirrorFingerprint(args: {
  name: string;
  message: string;
  env?: string;
  mode?: RuntimeMode;
  host?: string;
  pathname?: string;
  top?: { filename?: string; function?: string } | undefined;
}) {
  const normalizedMsg = args.message.replace(/\d+/g, "#");
  const parts = [
    MIRROR_PREFIX,
    args.name,
    normalizedMsg,
    args.mode,
    args.host,
    args.pathname,
    args.env,
    args.top?.filename,
    args.top?.function,
  ].filter(Boolean);
  return parts.join("|");
}

/**
 * AIDEV-NOTE: Extract first exception block from Sentry event for canonical fields.
 */
function getFirstException(event: SentryEvent) {
  return event.exception?.values?.[0];
}

/**
 * AIDEV-NOTE: Extract stack frames (may be missing); used for fingerprint + stack lines.
 */
function getFrames(first: Exception | undefined): SentryFrame[] | undefined {
  const frames = first?.stacktrace?.frames as SentryFrame[] | undefined;
  return Array.isArray(frames) ? frames : undefined;
}

/**
 * AIDEV-NOTE: Coalesce name/message/stack; prefers original Error for accuracy in tracking.
 */
function deriveNameAndMessage(
  original: unknown,
  first: Exception | undefined,
  fallbackMessage?: string
): { name: string; message: string; stackFromOriginal?: string } {
  const defaultName = (first?.type as string) || "Error";
  const defaultMsg =
    (first?.value as string) || fallbackMessage || "Unhandled error";
  if (original instanceof Error) {
    return {
      name: original.name || defaultName,
      message: original.message || defaultMsg,
      stackFromOriginal: original.stack,
    };
  }
  return { name: defaultName, message: defaultMsg };
}

/**
 * AIDEV-NOTE: Convert frames to V8-like lines; improves PostHog parsing and fingerprint stability.
 */
function v8StackLines(frames?: SentryFrame[]): string[] | undefined {
  if (!frames || !frames.length) return undefined;
  const ordered = [...frames].reverse();
  return ordered.map((f) => {
    const fn = f.function || "<anonymous>";
    const file = f.filename || "<unknown>";
    const line = f.lineno ?? "?";
    const col = f.colno ?? "?";
    return `    at ${fn} (${file}:${line}:${col})`;
  });
}

/**
 * AIDEV-NOTE: Ensure we send a real Error object; rebuild when Sentry provided only payload.
 */
function ensureErrorObject(
  original: unknown,
  name: string,
  message: string,
  stackLines?: string[]
): Error {
  if (original instanceof Error) return original;
  const err = new Error(message);
  try {
    err.name = name;
    if (stackLines && stackLines.length)
      err.stack = `${name}: ${message}\n${stackLines.join("\n")}`;
  } catch {
    // ignore
  }
  return err;
}

/**
 * AIDEV-NOTE: Pick the most-informative top frame (file/function) to seed fingerprint.
 */
function getTopFrameInfo(
  frames?: SentryFrame[]
): { filename?: string; function?: string } | undefined {
  if (!frames || !frames.length) return undefined;
  const top = frames[frames.length - 1];
  return { filename: top.filename, function: top.function };
}
/**
 * AIDEV-NOTE: Prefer explicit runtime mode from tags; fallback to runtime detection.
 */
function runtimeModeFromTags(
  tags?: Record<string, unknown>
): RuntimeMode | undefined {
  const tagged = (tags?.mode as RuntimeMode | undefined) ?? undefined;
  return tagged ?? detectRuntimeMode();
}

/**
 * AIDEV-NOTE: Mode detection: window.self !== window.top implies embedded; otherwise standalone.
 * Cross-origin access may throw; in that case we assume embedded.
 */
function detectRuntimeMode(): RuntimeMode {
  if (typeof window === "undefined") return "standalone";
  try {
    const inIframe = window.self !== window.top;
    return inIframe ? "embedded" : "standalone";
  } catch {
    return "embedded";
  }
}

/**
 * AIDEV-NOTE: Host metadata aids grouping/UI; avoid including query/secrets.
 */
function getWindowHost(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location?.host;
}

/**
 * AIDEV-NOTE: Path improves signal for route-specific errors without leaking params.
 */
function getWindowPathname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location?.pathname;
}
